/**
 * The single seat of review-window logic in the frontend.
 *
 * The list cell and the send-confirmation warning are two surfaces that have to agree
 * on "this expired". A second copy of the comparison is exactly how they stop agreeing,
 * so `isReviewWindowExpired` is derived from the same resolution the cell uses rather
 * than re-implementing it. Same remedy as `draft_view.py` in the backend, same reason.
 *
 * TWO RULES THAT ARE NOT STYLE:
 *
 * 1. `now` is a PARAMETER, not an embedded `new Date()`. Without it none of this is
 *    deterministically testable, and a test that depends on CI's clock fails on a
 *    Tuesday.
 * 2. The only date this module reads for the countdown is `review_window_expires_at`.
 *    It never touches `created_at` -- regenerating starts a new execution with a new
 *    clock while `created_at` stays put, and deriving the window from it was four days
 *    wrong on a real record, in the direction that makes a doctor hurry.
 *    `reviewed_at` is read only to compare two dates that are both already in the past
 *    (the "reviewed out of window" marker), never to compute time remaining.
 */

import { format, formatDistanceStrict } from 'date-fns';
import { es } from 'date-fns/locale';

import { Interpretation, InterpretationStatus } from '../types';

/**
 * The one threshold that separates "plan it" from "do it now". 48 h is what survives a
 * weekend or a day away; below that the case cannot be scheduled, it has to be handled.
 * A design value, not a measurement -- which is why it lives here: moving it is the
 * whole conversation about urgency.
 */
export const URGENT_THRESHOLD_HOURS = 48;

export type ReviewWindowLevel =
  | 'normal' // window open, more than the threshold left
  | 'urgent' // window open, threshold or less left
  | 'expired' // still waiting for review, but the window already closed
  | 'unknown' // waiting for review and there is NO window data -- say so, never guess
  | 'pending-start' // the execution has not reached the review step yet
  | 'late' // reviewed after the window had already closed
  | 'none'; // no review window applies to this row

export interface ReviewWindowCell {
  level: ReviewWindowLevel;
  /** What the cell reads. Carries the distinction on its own, without any colour. */
  label: string;
  /** The tooltip. Every row has one: a bare cell is indistinguishable from a bug. */
  title: string;
}

const REVIEWED_STATUSES: InterpretationStatus[] = [
  'approved',
  'approved_with_edit',
  'rejected',
];

/** Exact moment, spelled out, in the auditor's own timezone. */
function exact(date: Date): string {
  return format(date, "d MMM yyyy, HH:mm", { locale: es });
}

function parse(value?: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * How much is left, in words.
 *
 * The unit is forced by which side of the threshold we are on, and that is the point:
 * it is what makes "Quedan 3 días" and "Quedan 47 horas" read differently in black and
 * white. Left to pick its own unit, date-fns would render 50 h and 47 h as the same
 * "2 días", and the urgency would live only in the colour -- which is no use to a
 * colour-blind auditor on a bad screen.
 */
function remainingText(expiresAt: Date, now: Date, urgent: boolean): string {
  return formatDistanceStrict(expiresAt, now, {
    locale: es,
    unit: urgent ? 'hour' : 'day',
    roundingMethod: 'floor',
  });
}

/**
 * Everything the review-window cell of a row says: level, text and tooltip.
 *
 * @param interpretation The row being rendered.
 * @param now Injected clock. Defaults to the real one.
 */
export function reviewWindowCell(
  interpretation: Pick<
    Interpretation,
    'status' | 'reviewed_at' | 'review_window_started_at' | 'review_window_expires_at'
  >,
  now: Date = new Date()
): ReviewWindowCell {
  // Same default the row already applies: /interpretations/pending omits `status`
  // because every item in it is pending by construction.
  const status: InterpretationStatus = interpretation.status || 'pending_review';
  const expiresAt = parse(interpretation.review_window_expires_at);

  // Checked BEFORE the expiry on purpose: a regenerated record sits in `processing`
  // still carrying the previous execution's window until the new `store_token` runs.
  // Counting down that stale value would be a countdown on a clock that is not running.
  if (status === 'processing') {
    return {
      level: 'pending-start',
      label: 'En proceso',
      title: 'La ventana de revisión aún no ha empezado',
    };
  }

  if (REVIEWED_STATUSES.includes(status)) {
    const reviewedAt = parse(interpretation.reviewed_at);
    if (reviewedAt && expiresAt && reviewedAt > expiresAt) {
      // The most useful row in the whole feature: an approval that landed after the
      // window closed is a report that probably never reached the patient.
      return {
        level: 'late',
        label: 'Fuera de plazo',
        title: `Se revisó el ${exact(reviewedAt)}, después de que la ventana venciera el ${exact(
          expiresAt
        )}`,
      };
    }
    return {
      level: 'none',
      label: '—',
      title: 'La ventana de revisión terminó al revisarse',
    };
  }

  if (status === 'pending_review') {
    if (!expiresAt) {
      // Declared, never estimated. A number derived from `created_at` here would look
      // exactly like a real deadline and be wrong by every regeneration.
      return {
        level: 'unknown',
        label: 'Sin dato',
        title: 'No se registró el inicio de la ventana de revisión',
      };
    }

    if (expiresAt.getTime() <= now.getTime()) {
      return {
        level: 'expired',
        label: `Venció hace ${formatDistanceStrict(now, expiresAt, { locale: es })}`,
        title: `Venció el ${exact(expiresAt)}`,
      };
    }

    const hoursLeft = (expiresAt.getTime() - now.getTime()) / 3_600_000;
    const urgent = hoursLeft <= URGENT_THRESHOLD_HOURS;
    return {
      level: urgent ? 'urgent' : 'normal',
      label: `Quedan ${remainingText(expiresAt, now, urgent)}`,
      title: `Vence el ${exact(expiresAt)}`,
    };
  }

  // `error`, `timeout`, and anything the backend adds later: a deliberate dash with an
  // explanation, never an empty cell that reads as "nothing happening here".
  return {
    level: 'none',
    label: '—',
    title: 'Sin ventana de revisión',
  };
}

/**
 * What the send confirmation asks before letting an approval through.
 *
 * Derived from `reviewWindowCell` rather than re-comparing the dates: the warning in the
 * dialog and the cell in the list must never be able to disagree about whether this
 * record's window is closed.
 */
export function isReviewWindowExpired(
  interpretation: Pick<
    Interpretation,
    'status' | 'reviewed_at' | 'review_window_started_at' | 'review_window_expires_at'
  >,
  now: Date = new Date()
): boolean {
  return reviewWindowCell(interpretation, now).level === 'expired';
}
